/**
 * 브라우저 번역 확장(Google 번역 등)은 React가 관리하는 텍스트 노드를 `<font>` 등으로
 * 감싸 다른 부모로 옮긴다. 이후 React 가 reconciliation 과정에서 그 노드를
 * removeChild/insertBefore 하려 하면 "노드가 이 부모의 자식이 아니다"라며 NotFoundError 가
 * 발생해 화면 전체가 크래시한다. (맨 텍스트가 형제 엘리먼트와 섞인 노드가 교체·삭제될 때 발현)
 *
 * 부모가 일치하지 않는 경우 예외를 내는 대신 무시하도록 두 메서드를 방어적으로 감싼다.
 * 정상 경로(노드가 실제 자식인 경우)는 그대로 위임하므로 동작이 바뀌지 않는다.
 * 우리 코드의 실제 DOM 버그를 가리지 않도록 개발 모드에서는 경고를 남긴다.
 */
export const installBrowserTranslationGuard = () => {
  if (typeof Node !== 'function' || !Node.prototype) return

  const warn = (method: string, node: Node, parent: Node) => {
    if (import.meta.env.DEV) {
      console.warn(`[browserTranslationGuard] ${method} 부모 불일치로 무시됨`, node, parent)
    }
  }

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      warn('removeChild', child, this)
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      warn('insertBefore', referenceNode, this)
      return newNode
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}
